import { Article } from '../types';

export const oilAndGasArticles: Article[] = [
    {
        id: 'dangote-refinery-fuel-price-impact-2025',
        slug: 'dangote-refinery-impact-fuel-prices-nigeria',
        title: 'Dangote Refinery: Impact on Petrol Prices in 2025',
        excerpt: 'Analysis of Dangote Refinery\'s output in 2025. Is it actually reducing the pump price of PMS and Diesel? We look at the data.',
        category: 'Oil & Gas',
        date: 'December 2, 2025',
        readTime: '3 min read',
        author: 'Oil & Gas Analysis Team',
        trending: false,
        keywords: ['Dangote refinery fuel price', 'PMS price 2025', 'Aliko Dangote refinery'],
        metaDescription: 'Has the Dangote Refinery reduced fuel prices in Nigeria? 2025 analysis of production output, pricing strategy, and market impact.',
        content: `The 650,000 barrels-per-day Dangote Refinery was touted as the savior of Nigeria's energy crisis. Now fully operational in 2025, we analyze its real impact on your wallet.

## The Pricing Reality
Contrary to popular belief, local refining does not automatically mean "cheap" fuel. The crude is sold to the refinery at international market rates (even if paid in Naira).
- **Logistics Savings:** The main saving is on freight (avoiding shipping to Europe and back) which accounts for ~15-20% of the cost.
- **Supply Stability:** The biggest benefit has been availability. Scarcity queues have reduced significantly.

## Diesel vs PMS
- **Diesel (AGO):** Prices have stabilised around ₦1,100-₦1,150/litre, down from highs of ₦1,700, thanks to massive domestic supply from Dangote.
- **Petrol (PMS):** Prices remain high due to crude oil costs and FX fluctuations, currently between ₦950 - ₦1,100 per litre across most states.

## Conclusion
Dangote Refinery guarantees **security of supply**, not necessarily rock-bottom prices. As of November 2025, the refinery is supplying an average of **23.52 million litres of petrol per day** and **5.596 million litres of diesel per day** to the Nigerian market. The refinery recently hit a milestone of **70 million litres combined daily output** (45M litres petrol + 25M litres diesel), exceeding Nigeria's domestic consumption needs. The era of ₦100/litre fuel is gone forever, but the era of buying fuel from neighboring countries is also over.

## Frequently Asked Questions

**Has Dangote Refinery reduced fuel prices in Nigeria?**
Not significantly. While local refining saves 15-20% on freight logistics, crude oil is still sold to the refinery at international market rates. Petrol prices remain ₦950-₦1,100/litre due to crude costs and FX fluctuations. The main benefit is supply stability — fuel scarcity queues have reduced dramatically.

**How much petrol does Dangote Refinery produce per day?**
As of November 2025, the Dangote Refinery produces an average of **23.52 million litres of petrol (PMS) per day** and **5.596 million litres of diesel (AGO) per day**. The refinery recently achieved a combined output milestone of 70 million litres daily (45M PMS + 25M AGO).

**When will Dangote fuel be available nationwide?**
Dangote fuel is already available nationwide through NNPC retail stations and independent marketers. The refinery is processing 100% Nigerian crude as of end-2025 and has the capacity to exceed Nigeria's domestic consumption needs.

**Why is diesel cheaper than petrol from Dangote?**
Diesel (AGO) prices stabilised at ₦1,100-₦1,150/litre (down from ₦1,700 highs) due to massive domestic supply from Dangote. Diesel is also deregulated, allowing market forces to work. Petrol remains higher due to subsidy removal and higher global demand.

**What is Dangote Refinery's total capacity?**
The refinery has a processing capacity of 650,000 barrels per day (bpd), making it Africa's largest refinery. There are plans to expand to 700,000 bpd by late 2025 and potentially 1.4 million bpd within three years.

## Related Articles
- [Cooking Gas (LPG) Prices 2025: Monthly Trend Monitor](/articles/cooking-gas-lpg-prices-trend-2025)
- [Understanding Petrol Pump Price Breakdown](/articles/petrol-fual-pump-price-breakdown-nigeria)
- [Port Harcourt Refinery: Status Update 2025](/articles/port-harcourt-refinery-revival-status)
- [Track Live Refinery Data](/tools/refinery-tracker)`
    },
    {
        id: 'lpg-gas-prices-nigeria-2025',
        slug: 'cooking-gas-lpg-prices-trend-2025',
        title: 'Cooking Gas (LPG) Prices in Nigeria: Trend Monitor',
        excerpt: 'Track 12.5kg, 6kg, and 1kg LPG refill price trends in Nigeria, why cooking gas prices move, and how to buy safely.',
        category: 'Oil & Gas',
        date: 'November 15, 2025',
        dateModified: 'June 8, 2026',
        readTime: '5 min read',
        author: 'Consumer Price Watch',
        trending: false,
        keywords: ['cooking gas price today', '12.5kg gas price', 'LPG price Nigeria'],
        metaDescription: 'Cooking gas LPG price trend guide for Nigeria: 12.5kg, 6kg, and 1kg refill estimates, price drivers, safety checks, and buying tips.',
        faqs: [
            {
                question: 'Why does cooking gas price change so often in Nigeria?',
                answer: 'LPG prices move with exchange rates, international gas benchmarks, depot logistics, trucking costs, seasonal demand, and local supply disruptions.',
            },
            {
                question: 'Is cheaper cooking gas always safe?',
                answer: 'No. Extremely cheap gas can indicate poor measurement, questionable blending, or unsafe refill practices. Use reputable gas plants and check cylinder condition.',
            },
            {
                question: 'How do I check if a gas cylinder is expired?',
                answer: 'Look for the stamped month/quarter and year code on the cylinder handle or collar. Do not use expired, rusted, leaking, or damaged cylinders.',
            },
        ],
        content: `Cooking gas has become a major household expense. Prices move because LPG is exposed to exchange rates, international benchmarks, local depot logistics, trucking costs, and seasonal demand.

**Last verified:** June 2026. Treat all prices below as market ranges, not fixed national prices. Always check local refill stations before budgeting.

## Typical LPG Refill Price Ranges

- **12.5kg Cylinder:** Often priced as the main household benchmark and highly sensitive to depot and transport costs.
- **6kg Cylinder:** Usually used by smaller households and students; pricing can be less efficient per kg than a 12.5kg refill.
- **1kg Refill:** Common at neighborhood refill points, but measurement accuracy and safety standards vary widely.

## 2025 Price Trend
LPG prices peaked in June 2025 at an average of **₦21,010** per 12.5kg cylinder, then dropped to **₦17,408 in August** before climbing again to **₦18,637 in October**.

**Regional Variations (October 2025):**
- **Highest:** Borno (₦19,392), Yobe (₦19,340), Ondo (₦19,290)
- **Lowest:** Ebonyi (₦17,611), Kwara (₦17,800)

## Why the Price Moves
1. **FX Rate:** LPG is exposed to US Dollar pricing and import parity.
2. **Global Demand:** International LPG prices can rise during winter-demand cycles.
3. **Terminal Logistics:** Delays at depots and ports create local scarcity.
4. **Transport Costs:** States farther from terminals often pay more.
5. **Retail Measurement:** Some refill points underfill cylinders, making the headline price misleading.

## Safety Check
Cheap gas isn't always good. "Blended" gas with high propane content burns faster and can be dangerous in hot weather. Always buy from reputable gas plants.

## Frequently Asked Questions

**How much is 12.5kg cooking gas in Nigeria now?**
As of December 2025, a 12.5kg cooking gas cylinder costs between ₦18,000 and ₦21,000 depending on your location. The national average is ₦18,637 (October 2025 NBS data). Prices are highest in Borno (₦19,392) and lowest in Ebonyi (₦17,611).

**Why is cooking gas so expensive in Nigeria 2025?**
Three main reasons: (1) LPG is priced in US Dollars even for domestic gas, making it vulnerable to Naira devaluation, (2) Global winter demand (Q1) pushes prices up, and (3) Terminal logistics and delays at Lagos depots create artificial scarcity. Despite local NLNG production, pricing follows international benchmarks.

**When is the best time to buy cooking gas?**
Prices typically peak in Q1 (Jan-Mar) due to global winter demand and drop in Q2/Q3 (Apr-Sep). In 2025, prices peaked at ₦21,010 in June, dropped to ₦17,408 in August, then rose again to ₦18,637 in October. Buy during price dips if you have storage capacity.

**How do I know if my gas cylinder has expired?**
Check the metal rim/handle for a stamped code (e.g., "D24"). The letter indicates quarter (A=Jan-Mar, B=Apr-Jun, C=Jul-Sep, D=Oct-Dec) and the number is the year. "D24" means expired in December 2024. Cylinders have a 15-year lifespan — never use an expired cylinder.

**Where can I buy cooking gas cheapest in Nigeria?**
Prices vary by state due to distance from terminals. Cheapest states in 2025 include Ebonyi (₦17,611), Kwara (₦17,800), and major cities with direct terminal access like Lagos and Abuja. Avoid roadside "blended" gas that burns faster.

## Related Articles
- [How to Check Your Gas Cylinder Expiry Date](/articles/how-to-check-gas-cylinder-expiry-date)
- [How to Start a Cooking Gas (LPG) Business](/articles/how-to-start-cooking-gas-business-nigeria)
- [Dangote Refinery Impact on Fuel Prices](/articles/dangote-refinery-impact-fuel-prices-nigeria)
- [Understanding Petrol Price Breakdown](/articles/petrol-fual-pump-price-breakdown-nigeria)`
    },
    {
        id: 'lpg-cylinder-expiry-safety',
        slug: 'how-to-check-gas-cylinder-expiry-date',
        title: 'Safety Alert: How to Check Your Gas Cylinder Expiry Date',
        excerpt: 'Gas cylinders expire! Learn how to read the hidden code on your cylinder rim to prevent explosions.',
        category: 'Oil & Gas',
        date: 'December 10, 2024',
        readTime: '2 min read',
        author: 'Safety Standards Team',
        trending: false,
        keywords: ['gas cylinder expiry date', 'check gas cylinder safety', 'LPG safety tips'],
        metaDescription: 'Do not use expired gas cylinders. Learn how to read the A, B, C, D expiry codes stamped on the metal rim to keep your home safe.',
        content: `Many Nigerians are unaware that LPG cylinders have a lifespan of 15 years. Using an expired cylinder is a ticking time bomb.

## How to Read the Code
Look at the inner side of the metal handle/rim. You will see a letter (A, B, C, D) followed by a number (e.g., 25).

- **The Letter (Quarter):**
  - **A:** Jan - Mar
  - **B:** Apr - Jun
  - **C:** Jul - Sep
  - **D:** Oct - Dec
- **The Number (Year):**
  - **25:** 2025
  - **30:** 2030

## Example
If you see **D24**, that cylinder expired in **December 2024**. Stop using it immediately!

## What to Do With Expired Cylinders
Do not sell it to a scrap dealer. Return it to a certified gas plant for safe decommissioning. Your life is worth more than the cost of a new cylinder (~₦25,000 for 12.5kg).`
    },
    {
        id: 'petrol-price-breakdown-nigeria',
        slug: 'petrol-fual-pump-price-breakdown-nigeria',
        title: 'Understanding the Pump Price: Breakdown of Petrol Costs',
        excerpt: 'Where does your ₦1,000 go? Breakdown of landing cost, distribution margins, retailers margin, and NMDPRA levies.',
        category: 'Oil & Gas',
        date: 'January 15, 2025',
        readTime: '2 min read',
        author: 'Energy Economics Team',
        trending: false,
        keywords: ['petrol price breakdown', 'landing cost pms', 'fuel subsidy structure'],
        metaDescription: 'Detailed breakdown of the petrol pump price in Nigeria. How much is landing cost, distribution, and marketer margins?',
        content: `Ever wondered why fuel costs what it costs? Here is the anatomy of a litre of PMS in a deregulated market.

## The Cost Components
1.  **Direct Crude Cost/Landing Cost (~80%):** The cost of the raw product (crude oil) + refining cost. This is tied to Brent Crude prices ($/barrel) and the Naira exchange rate.
2.  **Distribution Margins (~15%):**
    - **Bridging Fund (NMDPRA):** Money pooled to equalize prices across the country (so Sokoto pays similar to Lagos).
    - **Transporters Margin (NARTO):** Cost of trucking the fuel from depot to station.
3.  **Retailer Margin (~5%):** The profit for the filling station owner to run generators, pay staff, and maintain pumps.

## Why Prices Differ
Prices are cheapest in Lagos (closest to Dangote/Import terminals) and get progressively more expensive as you move North due to transport costs.`
    },
    {
        id: 'start-lpg-business-nigeria',
        slug: 'how-to-start-cooking-gas-business-nigeria',
        title: 'How to Start a Cooking Gas (LPG) Business in Nigeria',
        excerpt: 'Capital requirements, licensing steps, and equipment needed to start a skid plant or retail outlet.',
        category: 'Oil & Gas',
        date: 'February 20, 2025',
        readTime: '2 min read',
        author: 'SME Business Desk',
        trending: false,
        keywords: ['start gas business nigeria', 'LPG skid plant cost', 'dpr licence for gas'],
        metaDescription: 'Guide to starting an LPG business in Nigeria. Cost of 2.5 ton skid, DPR licensing requirements, and location strategy.',
        content: `The LPG market in Nigeria is booming as kerosene fades out. It's a profitable business with high daily turnover.

## Types of Setups
1.  **Retailer (Reseller):** Buy usage 50kg big bottles and refill small quantities. *Low capital (₦500k).*
2.  **Skid Plant (Standard):** Install a tank (2.5 tons or 5 tons) and dispenser. *Medium capital (₦15M - ₦25M).*
3.  **Gas Plant:** Large storage. *High capital.*

## Basic Requirements for a Skid Plant
1.  **Land:** Must not be under power lines and must have setbacks from road.
2.  **DPR/NMDPRA License:** "Approval to Construct" (ATC) and "License to Operate" (LTO).
3.  **Equipment:** Storage tank, pump, dispenser, fire extinguishers, and scales.
4.  **Training:** Staff must be trained on safety protocols to prevent fires.

## Profitability
Margins average ₦50 - ₦100 per kg. A busy plant selling 1 ton (1000kg) daily creates decent cash flow, but overheads (diesel for generator) are high.`
    },
    {
        id: 'fake-engine-oil-identification',
        slug: 'how-to-spot-fake-engine-oil-nigeria',
        title: 'How to Spot Fake Engine Oil and Save Your Car',
        excerpt: 'Fake lubricants are killing engines across Nigeria. Simple tests to verify if your Total, Mobil, or Oando oil is genuine.',
        category: 'Oil & Gas',
        date: 'March 5, 2025',
        readTime: '2 min read',
        author: 'Automotive Care Team',
        trending: false,
        keywords: ['fake engine oil', 'spot fake total oil', 'car engine failure'],
        metaDescription: 'How to identify fake engine oil in Nigeria. Check seals, smell, color, and texture to protect your car engine from knock.',
        content: `Using fake oil sludge your engine and leads to "knocking" within months. The market is flooded with re-packaged used oil.

## Physical Checks
1.  **The Seal:** Genuine oil has a tight, tamper-proof foil seal with branding. If it peels off too easily or looks glued, reject it.
2.  **The Smell:** Fresh oil has a distinct chemical smell. Fake oil often smells burnt (like fuel) because it is recycled waste oil.
3.  **The Color:** Dip your finger. New oil is golden/amber and clear. Dark or black oil is used.
4.  **Texture:** Rub it between fingers. It should be smooth and slippery, not gritty.

## Buy from Official Stations
The only 100% guarantee is to buy from the filling station of the brand (e.g., buy Total oil at a Total station). Market stalls and roadside mechanics are high-risk sources.`
    },
    {
        id: 'port-harcourt-refinery-update',
        slug: 'port-harcourt-refinery-revival-status',
        title: 'Port Harcourt Refinery: Status Update 2025',
        excerpt: 'Is it working yet? Update on the endless turnaround maintenance of the PH refinery and what it means for the South-South.',
        category: 'Oil & Gas',
        date: 'April 12, 2025',
        readTime: '2 min read',
        author: 'Oil & Gas Analysis Team',
        trending: false,
        keywords: ['Port Harcourt refinery status', 'refinery rehabilitation nigeria', 'NNPC refineries'],
        metaDescription: '2025 Update on the Port Harcourt Refinery rehabilitation. Is it producing PMS? Impact on fuel availability in the South-South.',
        content: `The rehabilitation of the Port Harcourt Refining Company (PHRC) has been a headline for years.

## Current Status (Mid-2025)
Area 5 (Old Refinery) has achieved mechanical completion and is running test loads. However, full commercial production of PMS remains intermittent.

## Why it Matters
Dangote is in Lagos. Moving fuel to the South-South and North costs money. A functional PH refinery would crash logistics costs for the Eastern region coverage areas (Rivers, Aba, Enugu).

## The skepticism
Nigerians remain skeptical after decades of "Turn Around Maintenance" (TAM) with zero results. 2025 is the make-or-break year for NNPC to prove it can run a refinery.`
    },
    {
        id: 'diesel-price-impact-business',
        slug: 'diesel-ago-price-impact-business-costs',
        title: 'Current Diesel Prices and Impact on Business Costs',
        excerpt: 'Diesel is the lifeblood of Nigerian manufacturing. Tracking the price of AGO and how businesses are coping.',
        category: 'Oil & Gas',
        date: 'September 22, 2025',
        readTime: '2 min read',
        author: 'Business Economics Desk',
        trending: false,
        keywords: ['diesel price nigeria', 'cost of running business', 'AGO price today'],
        metaDescription: 'How the current price of Diesel (AGO) is affecting Nigerian businesses. Strategies for cost reduction including solar migration.',
        content: `For Nigerian businesses, Diesel (AGO) is often a bigger cost than rent or salaries.

## Price Monitor 2025
**Average:** ₦1,150 per litre.
While stable compared to the volatility of 2024, it remains historically high.

## Impact on Sectors
1.  **Logistics:** Haulage costs have tripled food prices.
2.  **Manufacturing:** Factory production costs have surged, making Nigerian goods less competitive.
3.  **SMEs:** Hotels and serviced apartments are struggling to maintain 24/7 power without raising rates to unsustainable levels.

## Survival Strategy
Businesses are aggressively moving to **Gas Generators** (CNG/LPG) which offer 40% savings, or **Solar Hybrids** for daytime operations to kill diesel consumption.`
    },
    {
        id: 'pipeline-vandalism-economic-cost',
        slug: 'oil-theft-pipeline-vandalism-nigeria-economy',
        title: 'Crude Oil Theft: The Economic Cost to Nigeria',
        excerpt: 'How much are we losing? The staggering numbers behind pipeline vandalism and oil bunkering in the Niger Delta.',
        category: 'Oil & Gas',
        date: 'June 18, 2025',
        readTime: '2 min read',
        author: 'Policy & Governance Team',
        trending: false,
        keywords: ['oil theft nigeria', 'pipeline vandalism', 'crude oil production'],
        metaDescription: 'The impact of crude oil theft and pipeline vandalism on Nigeria\'s economy. Analysis of production losses and environmental damage.',
        content: `Nigeria loses an estimated 200,000 to 400,000 barrels of crude oil *daily* to theft. This is equivalent to losing the entire GDP of a small country every year.

## How it Happens
"Illegal Bunkering" involves tapping into high-pressure pipelines to siphon crude. This is then cooked in illegal bush refineries or barged out to sea for black market export.

## The Consequences
1.  **Revenue Loss:** Less money for schools, roads, and grid infrastructure.
2.  **Environmental Disaster:** The soot in Port Harcourt and oil spills in the Delta destroy farming and fishing livelihoods.
3.  **Divestment:** Major IOCs (Shell, ExxonMobil) are selling their onshore assets to flee the insecurity.`
    },
    {
        id: 'nnpc-limited-structure-explained',
        slug: 'nnpc-limited-pia-structure-explained',
        title: 'NNPC Limited: What the New Commercial Structure Means',
        excerpt: 'NNPC is no longer a government corporation but a limited liability company. What changed with the PIA?',
        category: 'Oil & Gas',
        date: 'July 30, 2025',
        readTime: '2 min read',
        author: 'Energy Policy Team',
        trending: false,
        keywords: ['NNPC limited meaning', 'Petroleum Industry Act', 'Kyari NNPC'],
        metaDescription: 'Explaining NNPC Limited. How the Petroleum Industry Act (PIA) transitioned NNPC from a state corporation to a commercial profit-driven entity.',
        content: `The Petroleum Industry Act (PIA) transformed the NNPC into **NNPC Limited**.

## Key Changes
1.  **Profit Motive:** It is now mandated to make profit and declare dividends to its shareholders (the Federation), rather than just remitting revenue.
2.  **No More Federal Funding:** Take out its own loans and fund its own operations. It can no longer rely on government appropriations.
3.  **Subsidy Payment:** Technically, NNPC Ltd should not pay subsidy. It bills the Federal Government for any under-recovery.

## Why it Matters
A commercially run NNPC should be more efficient, transparent, and able to compete with Saudi Aramco or Petrobras. However, political interference remains a significant challenge to this vision.`
    }
];
